import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from './Spinner';
import { apiService } from '../services/apiService';
import { AiPrompts, AiPromptKey } from '../types';
import { logger } from '../services/logger';
import { ConfirmModal } from './ConfirmModal';

interface PromptManagerProps {
    title: string;
}

const PROMPT_CONFIG: Record<AiPromptKey, { label: string; description: string; placeholders: string[] }> = {
    receiptExtractionSystemInstruction: {
        label: "Systémová instrukce pro extrakci dat",
        description: "Definuje roli a základní chování AI při analýze účtenek. Tento text je poslán před hlavním dotazem a nastavuje kontext.",
        placeholders: [],
    },
    receiptExtraction: {
        label: "Prompt pro extrakci dat z účtenky",
        description: "Hlavní dotaz, který instruuje AI, aby extrahovala data z obrázku/PDF podle interního schématu.",
        placeholders: [],
    },
    receiptExtractionJsonSchema: {
        label: "JSON Schéma pro extrakci dat",
        description: "Definuje JSON strukturu, kterou má AI vrátit, včetně DPH pro jednotlivé položky. Změny v tomto schématu vyžadují odpovídající úpravy v kódu, který data zpracovává. Upravujte pouze pokud přesně víte, co děláte.",
        placeholders: [],
    },
    shortenBusinessName: {
        label: "Zkrácení názvu podniku",
        description: "Prompt pro zkrácení dlouhých nebo formálních názvů podniků na kratší, čitelnou formu.",
        placeholders: ["{{fullName}}"],
    },
    suggestColors: {
        label: "Návrh barev pro kategorie/štítky",
        description: "Prompt pro AI, aby navrhla sémanticky vhodné barvy pro seznam položek (např. 'Potraviny' -> modrá).",
        placeholders: ["{{items}}"],
    },
    suggestMerges: {
        label: "Návrh na sloučení položek",
        description: "Prompt pro AI, aby nalezla podobné názvy v seznamu (např. 'Kategorie', 'Štítky') a navrhla jejich sloučení.",
        placeholders: ["{{items}}"],
    },
    groupProducts: {
        label: "Seskupení produktů na účtenkách",
        description: "Prompt pro AI, aby analyzovala seznam názvů produktů z účtenek a seskupila podobné (např. 'Mléko polot.' a 'Polotučné mléko').",
        placeholders: ["{{productNames}}"],
    },
};

const PROMPT_ORDER: AiPromptKey[] = [
    'receiptExtractionSystemInstruction',
    'receiptExtraction',
    'receiptExtractionJsonSchema',
    'shortenBusinessName',
    'suggestColors',
    'suggestMerges',
    'groupProducts',
];

const PromptEditor: React.FC<{
    promptKey: AiPromptKey;
    value: string;
    onChange: (key: AiPromptKey, value: string) => void;
    onReset: (key: AiPromptKey) => void;
}> = ({ promptKey, value, onChange, onReset }) => {
    const config = PROMPT_CONFIG[promptKey];
    const isJson = promptKey === 'receiptExtractionJsonSchema';
    const [internalValue, setInternalValue] = useState(value);
    
    useEffect(() => {
        if (isJson) {
            try {
                const parsed = JSON.parse(value);
                setInternalValue(JSON.stringify(parsed, null, 2));
            } catch {
                setInternalValue(value);
            }
        } else {
            setInternalValue(value);
        }
    }, [value, isJson]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setInternalValue(newValue);
        onChange(promptKey, newValue);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        if (isJson) {
            try {
                const parsed = JSON.parse(e.target.value);
                setInternalValue(JSON.stringify(parsed, null, 2));
            } catch {
                // Ponecháme neplatný JSON tak, jak je, aby ho mohl uživatel opravit
            }
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex justify-between items-center mb-1">
                 <h4 className="text-lg font-semibold text-gray-800">{config.label}</h4>
                 <button
                    onClick={() => onReset(promptKey)}
                    className="text-xs font-medium text-blue-600 hover:underline focus:outline-none"
                    aria-label={`Obnovit výchozí pro ${config.label}`}
                >
                    Obnovit výchozí
                </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">{config.description}</p>
            <textarea
                value={internalValue}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full h-48 p-3 font-mono text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            {config.placeholders.length > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                    <span className="font-semibold">Dostupné zástupné symboly:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                        {config.placeholders.map(p => <code key={p} className="bg-gray-200 px-2 py-1 rounded">{p}</code>)}
                    </div>
                </div>
            )}
        </div>
    );
};


export const PromptManager: React.FC<PromptManagerProps> = ({ title }) => {
    const [prompts, setPrompts] = useState<AiPrompts | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

    const loadPrompts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const userPrompts = await apiService.getAiPrompts();
            setPrompts(userPrompts);
        } catch (e: any) {
            logger.error("Nepodařilo se načíst AI prompty:", e);
            setError("Nepodařilo se načíst prompty. Zkuste to prosím znovu.");
        } finally {
            setIsLoading(false);
        }
    }, []);
    
    useEffect(() => {
        loadPrompts();
    }, [loadPrompts]);

    const handlePromptChange = useCallback((key: AiPromptKey, value: string) => {
        setPrompts(prev => prev ? { ...prev, [key]: value } : null);
    }, []);

    const handleSave = async () => {
        if (!prompts) return;
        setIsSaving(true);
        setError(null);
        try {
            await apiService.updateAiPrompts(prompts);
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2500);
        } catch (e: any) {
            logger.error("Nepodařilo se uložit AI prompty:", e);
            setError("Nepodařilo se uložit prompty.");
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleResetAll = async () => {
        setIsResetConfirmOpen(false);
        setIsLoading(true);
        setError(null);
        try {
            const defaultPrompts = await apiService.getDefaultAiPrompts();
            setPrompts(defaultPrompts);
        } catch (e: any) {
             logger.error("Nepodařilo se načíst výchozí AI prompty:", e);
            setError("Nepodařilo se obnovit výchozí nastavení.");
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleResetSinglePrompt = useCallback(async (keyToReset: AiPromptKey) => {
        try {
            const defaultPrompts = await apiService.getDefaultAiPrompts();
            if (defaultPrompts && defaultPrompts[keyToReset] !== undefined) {
                handlePromptChange(keyToReset, defaultPrompts[keyToReset]);
            } else {
                logger.warn(`Nepodařilo se nalézt výchozí hodnotu pro prompt: ${keyToReset}`);
            }
        } catch (e: any) {
            logger.error(`Nepodařilo se načíst výchozí prompty pro reset:`, e);
            setError("Nepodařilo se obnovit výchozí hodnotu.");
        }
    }, [handlePromptChange]);

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold text-gray-700">{title}</h3>
                <p className="text-sm text-gray-600 mt-2">
                    Zde můžete upravit texty (prompty), které aplikace posílá umělé inteligenci. Změny se projeví okamžitě pro všechny budoucí AI operace.
                    Buďte opatrní, nevhodné změny mohou ovlivnit funkčnost aplikace.
                </p>
            </div>

            {isLoading && <Spinner text="Načítám prompty..." />}
            {error && <div className="p-3 bg-red-100 text-red-700 text-sm rounded-md border border-red-200">{error}</div>}

            {prompts && (
                 <div className="space-y-6">
                    {PROMPT_ORDER.map(key => (
                         prompts[key] !== undefined && (
                            <PromptEditor 
                                key={key}
                                promptKey={key}
                                value={prompts[key]}
                                onChange={handlePromptChange}
                                onReset={handleResetSinglePrompt}
                            />
                         )
                    ))}
                </div>
            )}
            
            <div className="bg-white p-6 rounded-lg shadow sticky bottom-4 z-10">
                 <div className="flex items-center gap-4">
                    <button
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                    >
                        {isSaving ? 'Ukládám...' : 'Uložit všechny změny'}
                    </button>
                    <button
                        onClick={() => setIsResetConfirmOpen(true)}
                        disabled={isSaving || isLoading}
                        className="text-sm text-gray-600 hover:underline disabled:opacity-50"
                    >
                        Obnovit vše na výchozí
                    </button>
                    {showSuccess && (
                        <span className="text-green-600 font-medium transition-opacity duration-300">
                            Změny byly úspěšně uloženy!
                        </span>
                    )}
                </div>
            </div>
             <ConfirmModal
                isOpen={isResetConfirmOpen}
                onClose={() => setIsResetConfirmOpen(false)}
                onConfirm={handleResetAll}
                title="Obnovit všechny prompty?"
                confirmText="Ano, obnovit"
                confirmClassName="bg-orange-600 text-white rounded-md hover:bg-orange-700"
            >
                <p>Opravdu chcete obnovit VŠECHNY AI prompty na jejich výchozí hodnoty? Vaše vlastní úpravy budou ztraceny.</p>
            </ConfirmModal>
        </div>
    );
};